export interface PriceBookItemQueryResponse {
  id: string
  name: string
  type: string
  default_rate: number | null
  description: string | null
  unit_of_measure: string | null
  is_archived: boolean
  associated_hub_id: string | null
  associated_resource_type_ids: number[]
  pickup_location_ids: number[]
  destination_location_ids: number[]
  pickup_location_tag_ids: number[]
  destination_location_tag_ids: number[]
  valid_from: string | null
  valid_until: string | null
}

/**
 * Price book item types, keyed by their `PriceBookItemType` enum name in
 * `IronSight.Domain/Invoices/PriceBookItem.cs` and in the same order. Values
 * are the enum's `[Description]`s, which the API sends as an item's `type`.
 */
export const PRICE_BOOK_ITEM_TYPES = {
  Product: 'Product (Other)',
  Service: 'Service (Other)',
  AccessorialEquipmentHourly: 'Accessorial charge (Equipment • Hourly)',
  AccessorialEquipmentFlat: 'Accessorial charge (Equipment • Flat rate)',
  AccessorialFuel: 'Accessorial charge (Fuel)',
  AccessorialThirdParty: 'Accessorial charge (Third-party)',
  AccessorialOther: 'Accessorial charge (Other)',
  ProductParts: 'Product (Parts & materials)',
  ProductThirdParty: 'Product (Third-party)',
  ServiceFlatRate: 'Service (Flat rate)',
  ServiceHourlyRegular: 'Service (Hourly labour • Regular)',
  ServiceHourlyOvertime: 'Service (Hourly labour • Overtime)',
  ServiceHourlyStandby: 'Service (Hourly labour • Standby time)',
  ServiceHourlyTravel: 'Service (Hourly labour • Travel time)',
  ServiceTransportVolume: 'Service (Transport • Volume)',
  ServiceTransportDistance: 'Service (Transport • Distance)',
  ServiceThirdParty: 'Service (Third-party)',
  ProductSerializedParts: 'Product (Serialized parts)',
} as const

export type PriceBookItemType = (typeof PRICE_BOOK_ITEM_TYPES)[keyof typeof PRICE_BOOK_ITEM_TYPES]

const KNOWN_TYPES = new Set<string>(Object.values(PRICE_BOOK_ITEM_TYPES))

/**
 * A price book item's `type` is free text on the backend. Items saved through
 * the admin portal store the description (`"Service (Hourly labour • Regular)"`),
 * older rows the enum name (`"ServiceHourlyRegular"`), and CSV-synced items
 * whatever the file held. Anything unrecognised is treated as having no type.
 */
export function parsePriceBookItemType(type: string | null | undefined): PriceBookItemType | null {
  const trimmed = type?.trim()
  if (!trimmed) return null
  if (KNOWN_TYPES.has(trimmed)) return trimmed as PriceBookItemType
  return Object.prototype.hasOwnProperty.call(PRICE_BOOK_ITEM_TYPES, trimmed)
    ? PRICE_BOOK_ITEM_TYPES[trimmed as keyof typeof PRICE_BOOK_ITEM_TYPES]
    : null
}
